import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://www.cherokeeyouthwarriors.com';

const sponsorsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Become a Sponsor | Cherokee Youth Warriors',
  description: 'Partner with Cherokee Youth Football & Cheer in Canton, GA. Sponsorship tiers available — Gold, Silver, and Bronze. Support youth athletics in your community.',
  url: `${siteUrl}/sponsors`,
  isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Cherokee Youth Warriors' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Sponsors', item: `${siteUrl}/sponsors` },
    ],
  },
};

export default function SponsorsPage() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    interestLevel: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => { formBody.append(key, value); });
      const response = await fetch('https://readdy.ai/api/form/d6hft4cbcbcsu13rg6q0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', businessName: '', email: '', phone: '', interestLevel: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Become a Sponsor | Cherokee Youth Warriors Canton GA"
        description="Partner with Cherokee Youth Football & Cheer in Canton, GA. Gold, Silver, and Bronze sponsorship tiers available. Support youth athletics and make a lasting community impact."
        keywords="sponsor Cherokee Warriors, youth football sponsor Canton GA, youth sports sponsorship, community partner"
        canonical={`${siteUrl}/sponsors`}
        schemaJson={sponsorsSchema}
      />
      <Header />

      <section className="relative pt-32 pb-20" style={{ backgroundImage: 'url(https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/ad77b95b9b0e8e4fa9edada9defa38c2.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <i className="ri-trophy-line text-4xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight">
            BECOME A SPONSOR
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Partner with Cherokee Youth Football &amp; Cheer to make a lasting impact in our community. Your support helps shape the future of young athletes and cheerleaders.
          </p>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4">OUR SPONSORS</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto"></div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-16 text-center border-2 border-dashed border-gray-200">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-red-50 rounded-full mb-6">
              <i className="ri-star-line text-5xl text-red-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Your Logo Could Be Here!</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our sponsors will be featured here with pride. Check back soon to see the businesses and organizations supporting Cherokee youth athletics.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-gray-900 mb-4">SPONSORSHIP TIERS</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the level that works best for your business and make a difference in our community
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 border-2 border-yellow-300 shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-full mb-6">
                <i className="ri-vip-crown-line text-3xl text-yellow-900"></i>
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-2">GOLD</h3>
              <p className="text-yellow-800 font-bold mb-6">Premier Partnership</p>
              <ul className="space-y-3 text-gray-700">
                {['Large logo on team banners','Featured placement on website','Social media recognition','Game day announcements','VIP game day experience'].map(b => (
                  <li key={b} className="flex items-start"><i className="ri-check-line text-xl text-yellow-600 mr-3 mt-1"></i><span>{b}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border-2 border-gray-300 shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-400 rounded-full mb-6">
                <i className="ri-medal-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-2">SILVER</h3>
              <p className="text-gray-700 font-bold mb-6">Strong Support</p>
              <ul className="space-y-3 text-gray-700">
                {['Medium logo on team banners','Logo on website sponsors page','Social media shout-outs','Recognition at events'].map(b => (
                  <li key={b} className="flex items-start"><i className="ri-check-line text-xl text-gray-600 mr-3 mt-1"></i><span>{b}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border-2 border-orange-300 shadow-lg hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-400 rounded-full mb-6">
                <i className="ri-award-line text-3xl text-orange-900"></i>
              </div>
              <h3 className="text-3xl font-black text-gray-900 mb-2">BRONZE</h3>
              <p className="text-orange-800 font-bold mb-6">Community Partner</p>
              <ul className="space-y-3 text-gray-700">
                {['Logo on team banners','Listed on website','Social media recognition'].map(b => (
                  <li key={b} className="flex items-start"><i className="ri-check-line text-xl text-orange-600 mr-3 mt-1"></i><span>{b}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-4">GET STARTED TODAY</h2>
            <p className="text-lg text-gray-600">Interested in becoming a sponsor? Fill out the form below and we'll get back to you shortly.</p>
          </div>
          <form id="sponsor-inquiry-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">Your Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm" placeholder="John Smith" />
              </div>
              <div>
                <label htmlFor="businessName" className="block text-sm font-bold text-gray-900 mb-2">Business Name *</label>
                <input type="text" id="businessName" name="businessName" value={formData.businessName} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm" placeholder="Your Business LLC" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">Email Address *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm" placeholder="john@business.com" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2">Phone Number *</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm" placeholder="(555) 123-4567" />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="interestLevel" className="block text-sm font-bold text-gray-900 mb-2">Sponsorship Tier Interest *</label>
              <select id="interestLevel" name="interestLevel" value={formData.interestLevel} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm cursor-pointer">
                <option value="">Select a tier...</option>
                <option value="Gold">Gold - Premier Partnership</option>
                <option value="Silver">Silver - Strong Support</option>
                <option value="Bronze">Bronze - Community Partner</option>
                <option value="Custom">Custom - Let's discuss options</option>
              </select>
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">Additional Information</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none transition-colors text-sm resize-none" placeholder="Tell us more about your business and sponsorship goals..."></textarea>
              <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-4 px-8 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap">
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
            </button>
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                <p className="text-green-800 font-bold text-center"><i className="ri-check-line mr-2"></i>Thank you! We'll be in touch soon.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <p className="text-red-800 font-bold text-center"><i className="ri-error-warning-line mr-2"></i>Something went wrong. Please try again.</p>
              </div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
